// @flow
import { get, orderBy } from 'lodash'

import { PopularDestinations } from '@/constants/eps'
import { Location, Property } from '@/models'
import type { PriceCheckDataType } from '@/models/property'

export type APICollection<T> = {
  values: Array<T>,
  isPending: boolean,
  errors: *
}

export type APIObject<T> = {
  value: T,
  isPending: boolean,
  errors: *
}

const getAppleStore = (store: StoreState) => store.apple

export const sortedDropDownLocations = (store: StoreState): OptionsType<*> =>
  // $FlowFixMe
  orderBy(getAppleStore(store).locations.map(location => (
    {value: location.code, label: location.city}
  )), ['label'], ['asc'] )

export const getLocations = (store: StoreState): APICollection<Location> => ({
  values: getAppleStore(store).locations,
  isPending: getAppleStore(store)?.getLocationsPending || false,
  errors: getAppleStore(store)?.getLocationsError || null
})

export const getProperties = (store: StoreState): APICollection<Property> => ({
  values: getAppleStore(store).properties,
  isPending: getAppleStore(store)?.searchPropertiesPending || false,
  errors: getAppleStore(store)?.searchPropertiesError || null
})

export const getPriceCheck = (store: StoreState): APIObject<PriceCheckDataType> => ({
  value: getAppleStore(store).priceCheck,
  isPending: getAppleStore(store)?.priceCheckPending || false,
  errors: getAppleStore(store)?.priceCheckError || null
})

export const getCreateBooking = (store: StoreState): APIObject<CreateBookingDataType> => ({
  value: getAppleStore(store).createBooking,
  isPending: getAppleStore(store)?.createBookingPending || false,
  errors: getAppleStore(store)?.createBookingError || null
})

export const getPayBooking = (store: StoreState): APIObject<any> => ({
  value: getAppleStore(store).payBooking,
  isPending: getAppleStore(store)?.payBookingPending || false,
  errors: getAppleStore(store)?.payBookingError || null
})

export const locationsByCountry = (store: StoreState): Array<{title: string, options: Array<{ city: string, code: string }>}> =>
  Object.entries(
    getAppleStore(store).locations
      .filter(location => location.properties >= 5)
      .reduce(
        (acc, {code, city, country}) => {
          acc[country] = (country in acc)
            ? [{city, code}, ...acc[country]]
            : [{city, code}]
          return acc
        }, {}
      )
  ).map(
    ([key, value: Array<Location>]) => ({
      title: key,
      // $FlowFixMe
      options: value.sort((a, b) => a.city.localeCompare(b.city))
    })
  ).sort((a, b) => a.title.localeCompare(b.title))

export const bookLink = (store: StoreState): string => get(getAppleStore(store).priceCheck, 'links.book.href')

export const getStripePaymentInfo = (store: StoreState) => ({
  clientSecret: getAppleStore(store).createBooking?.client_secret,
  clientEmail: getAppleStore(store).createBooking?.client_email,
  totalAmount: getAppleStore(store).createBooking?.payment_amount,
  itineraryId: getAppleStore(store).createBooking?.itinerary_id,
})

export const getItinerary = (store: StoreState) => ({
  value: getAppleStore(store).checkItinerary,
  isPending: getAppleStore(store).checkItineraryPending,
  errors: getAppleStore(store).checkItineraryError
})

export const getCancelBooking = (store: StoreState) => ({
  value: getAppleStore(store).cancelBooking,
  isPending: getAppleStore(store).cancelBookingPending,
  errors: getAppleStore(store).cancelBookingError
})

export const getPopularLocations = (store: StoreState): {[string]: Array<Location>} => {
  const getByIds = (regions: Array<string>) => store.apple.locations
    .filter(location => regions.includes(location.code))

  return Object.fromEntries<string, Array<Location>>(
    PopularDestinations
      .map(({ name, regions }) => [name, getByIds(regions)])
      .filter(([, locations]) => locations.length > 0)
  )
}

export default getAppleStore
